import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Pencil, Trash2, X, Upload, FileText, History, Check, Sparkles, AlertTriangle } from "lucide-react";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { PageHeader } from "@/components/library/layout";
import { Badge } from "@/components/library/badges";
import { EmptyState } from "@/components/library/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/shared/ConfirmDelete";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { toast } from "sonner";
import Icon from "@/components/Icon";
import {
  useStore, list, create, update, remove,
  upsertIngredient, approveInvoice, computeRecipe, resolvePriceImpacts, ingredientMatchStatus,
  UNITS, pricePerBase, baseUnitOf,
} from "@/lib/dataStore";
import { uploadAttachment, getAttachmentUrl, compressImage } from "@/lib/storage";
import { supabase } from "@/lib/supabaseClient";
import { downloadRecipePDF } from "@/lib/exporters";

const kr = (n) => `${(Number(n) || 0).toLocaleString("sv-SE", { maximumFractionDigits: 2 })} kr`;
const krUnit = (n) => `${(Number(n) || 0).toLocaleString("sv-SE", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kr`;
const pct = (n) => `${(Number(n) || 0).toLocaleString("sv-SE", { maximumFractionDigits: 1 })} %`;
const newId = () => Math.random().toString(36).slice(2, 10);

const TABS = [
  { key: "ravaror", label: "Råvaror", icon: "Package" },
  { key: "recept", label: "Recept", icon: "ClipboardList" },
  { key: "fakturor", label: "Fakturor", icon: "FileText" },
];

export default function Foodcost() {
  useStore();
  const { currentRestaurant, currentOrganization, profile } = useSbAuth();
  const rid = currentRestaurant?.id;
  const actor = profile?.full_name || "Ägare";
  const loc = useLocation();
  const [tab, setTab] = useState(() => (TABS.some((t) => t.key === loc.state?.tab) ? loc.state.tab : "ravaror"));
  const [openNewRecipe, setOpenNewRecipe] = useState(loc.state?.openNew ? Date.now() : 0);
  useEffect(() => {
    if (loc.state?.tab && TABS.some((t) => t.key === loc.state.tab)) setTab(loc.state.tab);
    if (loc.state?.openNew) setOpenNewRecipe(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.key]);

  const ingredients = list(rid, "ingredients").filter((i) => (i.product_type || "ravara") !== "forbrukning");
  const recipes = list(rid, "recipes");
  const invoices = list(rid, "invoices");
  const impacts = list(rid, "price_impacts").filter((p) => !p.resolved);
  const affectedCount = new Set(impacts.flatMap((p) => p.affected_recipe_ids || [])).size;

  return (
    <div className="max-w-[1100px] mx-auto p-6 sm:p-10 animate-fade-up" data-testid="foodcost-page">
      <PageHeader title="Ekonomi"
        description="Håll råvarupriserna aktuella, bygg recept och se exakt matkostnad, foodcost-% och marginal i realtid." />

      {impacts.length > 0 && (
        <div data-testid="price-impact-banner" className="mb-8 rounded-2xl border border-[hsl(38_92%_50%/.35)] bg-[hsl(38_92%_50%/.08)] p-5 flex items-start gap-4">
          <AlertTriangle className="h-5 w-5 text-[hsl(38_92%_62%)] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold">Råvarupriser har ändrats — {affectedCount} recept påverkas</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {impacts.slice(0, 4).map((p) => (
                <li key={p.id} data-testid={`impact-${p.id}`}>
                  <span className="text-foreground font-medium">{p.ingredient_name}</span> har ändrats från {kr(p.old_price)}/{p.unit} till {kr(p.new_price)}/{p.unit}. {(p.affected_recipe_ids || []).length} recept påverkas.
                </li>
              ))}
              {impacts.length > 4 && <li>+ {impacts.length - 4} till…</li>}
            </ul>
          </div>
          <Button onClick={() => { const n = resolvePriceImpacts(rid, actor); toast.success(`${n} prisändring(ar) granskade — recept omräknade`); }}
            data-testid="recompute-recipes" className="bg-accent text-white hover:opacity-90 shrink-0">Räkna om recept</Button>
        </div>
      )}

      <div className="flex gap-1 border-b border-border mb-8 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`foodcost-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? "border-accent text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon name={t.icon} className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "ravaror" && <IngredientsTab rid={rid} ingredients={ingredients} actor={actor} />}
      {tab === "recept" && <RecipesTab rid={rid} recipes={recipes} ingredients={ingredients} actor={actor} openNewSignal={openNewRecipe} />}
      {tab === "fakturor" && <InvoicesTab rid={rid} invoices={invoices} actor={actor} />}
    </div>
  );
}

/* ---------------- Råvaror ---------------- */
function IngredientsTab({ rid, ingredients, actor }) {
  return (
    <div data-testid="ingredients-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="overline text-muted-foreground">{ingredients.length} råvaror</p>
        <IngredientEditor rid={rid} actor={actor} />
      </div>
      {ingredients.length === 0 ? <EmptyState title="Inga råvaror ännu" hint="Lägg till manuellt eller godkänn en faktura." /> : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="hidden md:grid grid-cols-[2fr_1.4fr_1fr_1fr_auto] gap-3 px-4 py-3 text-xs text-muted-foreground border-b border-border uppercase tracking-wide">
            <span>Råvara</span><span>Leverantör</span><span>Förpackning</span><span>Pris / enhet</span><span></span>
          </div>
          {ingredients.map((ing) => (
            <div key={ing.id} data-testid={`ingredient-row-${ing.id}`}
              className="grid grid-cols-2 md:grid-cols-[2fr_1.4fr_1fr_1fr_auto] gap-2 md:gap-3 px-4 py-3 border-b border-border last:border-0 items-center text-sm">
              <span className="font-medium">{ing.name}</span>
              <span className="text-muted-foreground truncate">{ing.supplier || "—"}</span>
              <span className="text-muted-foreground">{ing.pack_size} {ing.pack_unit} · {kr(ing.pack_price)}</span>
              <span className="font-mono">{krUnit(ing.price_per_base)}<span className="text-muted-foreground">/{ing.base_unit}</span></span>
              <div className="flex items-center gap-3 justify-end col-span-2 md:col-span-1">
                <IngredientHistory ing={ing} />
                <IngredientEditor rid={rid} actor={actor} existing={ing} />
                <ConfirmDelete title="Ta bort råvara?" description={`"${ing.name}" tas bort. Recept som använder den visar 0 kr på den raden.`}
                  onConfirm={() => { remove(rid, "ingredients", ing.id, actor); toast.success("Råvara borttagen"); }}
                  trigger={<button data-testid={`ingredient-delete-${ing.id}`} className="text-muted-foreground hover:text-[hsl(0_80%_72%)]"><Trash2 className="h-4 w-4" /></button>} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IngredientEditor({ rid, actor, existing }) {
  const [open, setOpen] = useState(false);
  const seed = () => existing
    ? { name: existing.name, supplier: existing.supplier || "", pack_size: existing.pack_size, pack_unit: existing.pack_unit, pack_price: existing.pack_price }
    : { name: "", supplier: "", pack_size: 1, pack_unit: "kg", pack_price: 0 };
  const [f, setF] = useState(seed);
  const ppb = pricePerBase(f.pack_price, f.pack_size, f.pack_unit);
  const save = () => {
    if (!f.name.trim()) return toast.error("Ange namn");
    upsertIngredient(rid, { id: existing?.id, ...f }, actor);
    toast.success(existing ? "Råvara uppdaterad" : "Råvara tillagd");
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(seed()); }}>
      <DialogTrigger asChild>
        {existing
          ? <button data-testid={`ingredient-edit-${existing.id}`} className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></button>
          : <Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" data-testid="new-ingredient"><Plus className="h-4 w-4 mr-1" />Ny råvara</Button>}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{existing ? "Redigera råvara" : "Ny råvara"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Namn</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ingredient-name" /></div>
          <div className="space-y-1.5"><Label>Leverantör</Label><Input value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} data-testid="ingredient-supplier" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Förp.storlek</Label><Input type="number" step="any" value={f.pack_size} onChange={(e) => setF({ ...f, pack_size: e.target.value })} data-testid="ingredient-size" /></div>
            <div className="space-y-1.5"><Label>Enhet</Label>
              <Select value={f.pack_unit} onValueChange={(v) => setF({ ...f, pack_unit: v })}><SelectTrigger data-testid="ingredient-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Pris (kr)</Label><Input type="number" step="any" value={f.pack_price} onChange={(e) => setF({ ...f, pack_price: e.target.value })} data-testid="ingredient-price" /></div>
          </div>
          <div className="rounded-xl bg-secondary/50 border border-border px-4 py-3 text-sm flex items-center justify-between">
            <span className="text-muted-foreground">Pris per {baseUnitOf(f.pack_unit)}</span>
            <span className="font-mono font-medium" data-testid="ingredient-ppb">{krUnit(ppb)}</span>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="save-ingredient">{existing ? "Spara" : "Lägg till"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IngredientHistory({ ing }) {
  const [open, setOpen] = useState(false);
  const hist = ing.price_history || [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`ingredient-history-${ing.id}`} className="text-muted-foreground hover:text-foreground" title="Prishistorik"><History className="h-4 w-4" /></button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Prishistorik — {ing.name}</DialogTitle></DialogHeader>
        {hist.length === 0 ? <p className="text-sm text-muted-foreground">Inga prisändringar registrerade ännu.</p> : (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 text-xs text-muted-foreground border-b border-border uppercase">
              <span>Datum</span><span>Gammalt</span><span>Nytt</span><span>Δ kr</span><span>Δ %</span>
            </div>
            {hist.map((h) => (
              <div key={h.id} data-testid={`price-change-${h.id}`} className="grid grid-cols-[1.2fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 text-sm border-b border-border last:border-0">
                <span className="text-muted-foreground font-mono text-xs">{new Date(h.date).toLocaleDateString("sv-SE")}</span>
                <span>{kr(h.old_price)}</span>
                <span className="font-medium">{kr(h.new_price)}</span>
                <span className={h.change_kr > 0 ? "text-danger" : "text-success"}>{h.change_kr > 0 ? "+" : ""}{kr(h.change_kr)}</span>
                <span className={h.change_pct > 0 ? "text-danger" : "text-success"}>{h.change_pct > 0 ? "+" : ""}{pct(h.change_pct)}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Recept ---------------- */
function foodcostTone(fc, target) {
  const t = target || 30;
  if (fc <= t) return "success";
  if (fc <= t + 5) return "warning";
  return "danger";
}

function RecipesTab({ rid, recipes, ingredients, actor, openNewSignal }) {
  return (
    <div data-testid="recipes-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="overline text-muted-foreground">{recipes.length} recept</p>
        <RecipeEditor rid={rid} ingredients={ingredients} actor={actor} startSignal={openNewSignal} />
      </div>
      {recipes.length === 0 ? <EmptyState title="Inga recept ännu" hint="Skapa ditt första recept och se matkostnaden direkt." /> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {recipes.map((r) => {
            const c = computeRecipe(rid, r);
            return (
              <div key={r.id} data-testid={`recipe-card-${r.id}`} className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-display font-bold tracking-tight">{r.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.portions} portion(er) · {kr(c.price)}</p>
                  </div>
                  <Badge tone={foodcostTone(c.foodCostPct, c.target)}>FC {pct(c.foodCostPct)}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="rounded-xl bg-secondary/50 py-2"><p className="text-[0.65rem] text-muted-foreground uppercase">Kostnad</p><p className="text-sm font-semibold">{kr(c.totalCost)}</p></div>
                  <div className="rounded-xl bg-secondary/50 py-2"><p className="text-[0.65rem] text-muted-foreground uppercase">Bruttovinst</p><p className="text-sm font-semibold">{kr(c.grossKr)}</p></div>
                  <div className="rounded-xl bg-secondary/50 py-2"><p className="text-[0.65rem] text-muted-foreground uppercase">Marginal</p><p className="text-sm font-semibold">{pct(c.grossPct)}</p></div>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <RecipeEditor rid={rid} ingredients={ingredients} actor={actor} existing={r} />
                  <button data-testid={`recipe-pdf-${r.id}`} onClick={() => downloadRecipePDF(r.name, c, `${c.portions} portion(er) · ${new Date().toLocaleDateString("sv-SE")}`)}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><FileText className="h-3.5 w-3.5" />PDF</button>
                  <ConfirmDelete title="Ta bort recept?" description={`"${r.name}" tas bort. Detta går inte att ångra.`}
                    onConfirm={() => { remove(rid, "recipes", r.id, actor); toast.success("Recept borttaget"); }}
                    trigger={<button data-testid={`recipe-delete-${r.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[hsl(0_80%_72%)]"><Trash2 className="h-3.5 w-3.5" />Ta bort</button>} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecipeEditor({ rid, ingredients, actor, existing, startSignal }) {
  const [open, setOpen] = useState(false);
  const seed = () => existing
    ? { name: existing.name, portions: existing.portions || 1, sale_price: existing.sale_price || 0, packaging_cost: existing.packaging_cost || 0, labor_cost: existing.labor_cost || 0, foodcost_target: existing.foodcost_target || 30, items: (existing.items || []).map((i) => ({ ...i })) }
    : { name: "", portions: 1, sale_price: 0, packaging_cost: 0, labor_cost: 0, foodcost_target: 30, items: [] };
  const [f, setF] = useState(seed);
  useEffect(() => { if (startSignal && !existing) { setF(seed()); setOpen(true); } }, [startSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  const c = computeRecipe(rid, f);
  const unitFor = (id) => ingredients.find((i) => i.id === id)?.pack_unit || "g";
  const setItem = (id, patch) => setF((s) => ({ ...s, items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) }));
  const addItem = () => setF((s) => ({ ...s, items: [...s.items, { id: newId(), ingredient_id: ingredients[0]?.id || "", qty: 100, unit: ingredients[0]?.pack_unit || "g" }] }));
  const removeItem = (id) => setF((s) => ({ ...s, items: s.items.filter((it) => it.id !== id) }));
  const save = () => {
    if (!f.name.trim()) return toast.error("Ange receptnamn");
    const payload = { ...f, name: f.name.trim() };
    if (existing) { update(rid, "recipes", existing.id, payload, actor); toast.success("Recept uppdaterat"); }
    else { create(rid, "recipes", { entity_type: "recipe", ...payload }, actor); toast.success("Recept skapat"); }
    setOpen(false);
  };
  const Row = ({ label, value, strong, tone }) => (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className={strong ? "font-medium" : "text-muted-foreground"}>{label}</span>
      <span className={`font-mono ${strong ? "font-semibold" : ""} ${tone === "danger" ? "text-danger" : tone === "success" ? "text-success" : ""}`}>{value}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(seed()); }}>
      <DialogTrigger asChild>
        {existing
          ? <button data-testid={`recipe-edit-${existing.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" />Redigera</button>
          : <Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" data-testid="new-recipe"><Plus className="h-4 w-4 mr-1" />Nytt recept</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Redigera recept" : "Nytt recept"}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-6">
          {/* left: inputs */}
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Receptnamn</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="recipe-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Portioner</Label><Input type="number" step="any" value={f.portions} onChange={(e) => setF({ ...f, portions: e.target.value })} data-testid="recipe-portions" /></div>
              <div className="space-y-1.5"><Label>Försäljningspris</Label><Input type="number" step="any" value={f.sale_price} onChange={(e) => setF({ ...f, sale_price: e.target.value })} data-testid="recipe-price" /></div>
              <div className="space-y-1.5"><Label>Förpackningskostnad</Label><Input type="number" step="any" value={f.packaging_cost} onChange={(e) => setF({ ...f, packaging_cost: e.target.value })} data-testid="recipe-packaging" /></div>
              <div className="space-y-1.5"><Label>Arbetskostnad</Label><Input type="number" step="any" value={f.labor_cost} onChange={(e) => setF({ ...f, labor_cost: e.target.value })} data-testid="recipe-labor" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label>Råvaror</Label>
                <Button type="button" size="sm" variant="secondary" onClick={addItem} data-testid="recipe-add-ingredient" disabled={ingredients.length === 0}><Plus className="h-4 w-4 mr-1" />Lägg till</Button></div>
              {ingredients.length === 0 && <p className="text-xs text-muted-foreground">Lägg till råvaror i fliken Råvaror först.</p>}
              <div className="space-y-2">
                {f.items.map((it, idx) => (
                  <div key={it.id} className="flex items-center gap-2" data-testid={`recipe-item-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <SearchableSelect
                        options={ingredients.map((ing) => ({ value: ing.id, label: ing.name, hint: ing.supplier }))}
                        value={it.ingredient_id}
                        onChange={(v) => setItem(it.id, { ingredient_id: v, unit: unitFor(v) })}
                        placeholder="Välj råvara" searchPlaceholder="Sök råvara…" emptyText="Ingen råvara hittad"
                        testid={`recipe-item-ingredient-${idx}`} />
                    </div>
                    <Input type="number" step="any" value={it.qty} onChange={(e) => setItem(it.id, { qty: e.target.value })} className="w-20" data-testid={`recipe-item-qty-${idx}`} />
                    <Select value={it.unit} onValueChange={(v) => setItem(it.id, { unit: v })}>
                      <SelectTrigger className="w-20" data-testid={`recipe-item-unit-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <button type="button" onClick={() => removeItem(it.id)} className="text-muted-foreground hover:text-[hsl(0_80%_72%)] shrink-0" data-testid={`recipe-item-remove-${idx}`}><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* right: live calc */}
          <div className="rounded-2xl border border-border bg-card p-5 h-fit" data-testid="recipe-breakdown">
            <p className="overline text-muted-foreground mb-3">Kalkyl</p>
            <Row label="Total råvarukostnad" value={kr(c.ingredientCost)} />
            <Row label="Förpackningskostnad" value={kr(c.packaging)} />
            <Row label="Arbetskostnad" value={kr(c.labor)} />
            <div className="border-t border-border my-1" />
            <Row label="Totalkostnad" value={kr(c.totalCost)} strong />
            <Row label="Försäljningspris" value={kr(c.price)} />
            <div className="border-t border-border my-1" />
            <Row label="Bruttovinst (kr)" value={kr(c.grossKr)} strong tone={c.grossKr < 0 ? "danger" : "success"} />
            <Row label="Bruttovinst (%)" value={pct(c.grossPct)} />
            <div className="flex items-center justify-between py-1.5 text-sm">
              <span className="font-medium flex items-center gap-1.5">Food Cost %{c.foodCostPct > (c.target || 30) && <AlertTriangle className="h-3.5 w-3.5 text-[hsl(0_80%_72%)]" />}</span>
              <Badge tone={foodcostTone(c.foodCostPct, c.target)} testid="recipe-foodcost">{pct(c.foodCostPct)}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Kostnad per portion: <span className="font-mono">{kr(c.costPerPortion)}</span> · mål {pct(c.target || 30)}</p>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="save-recipe">{existing ? "Spara ändringar" : "Skapa recept"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceThumb({ inv }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let ok = true;
    if (inv.photo_path) getAttachmentUrl(inv.photo_path, 600).then((u) => { if (ok) setUrl(u); }).catch(() => {});
    return () => { ok = false; };
  }, [inv.photo_path]);
  if (url) return <img src={url} alt="Faktura" className="h-10 w-10 rounded-lg object-cover border border-border shrink-0" data-testid={`invoice-thumb-${inv.id}`} />;
  return <FileText className="h-4 w-4 text-muted-foreground shrink-0" />;
}

function InvoiceDetail({ inv, open, onOpenChange }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let ok = true;
    if (open && inv.photo_path) getAttachmentUrl(inv.photo_path, 600).then((u) => { if (ok) setUrl(u); }).catch(() => {});
    return () => { ok = false; };
  }, [open, inv.photo_path]);
  const lines = inv.lines || [];
  const total = inv.total != null ? inv.total : lines.reduce((a, l) => a + (Number(l.pack_price) || 0) * (Number(l.qty) || 1), 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid={`invoice-detail-${inv.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />{inv.supplier || inv.file_name || "Faktura"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground -mt-1">
          <span>Godkänd {new Date(inv.created_at).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}</span>
          {inv.created_by && <span>· av {inv.created_by}</span>}
          <span>· {inv.line_count} rader</span>
        </div>
        <div className="grid md:grid-cols-[1fr_1.4fr] gap-5 mt-2">
          <div>
            {url ? (
              <a href={url} target="_blank" rel="noreferrer">
                <img src={url} alt="Faktura" className="w-full rounded-xl border border-border object-contain max-h-[420px] bg-secondary" data-testid={`invoice-detail-photo-${inv.id}`} />
              </a>
            ) : (
              <div className="w-full h-48 rounded-xl border border-dashed border-border grid place-items-center text-muted-foreground text-sm">Inget foto sparat</div>
            )}
            {url && <p className="text-xs text-muted-foreground mt-2 text-center">Klicka på bilden för att öppna i full storlek</p>}
          </div>
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="grid grid-cols-[1.6fr_.6fr_.9fr_1fr] gap-2 px-3 py-2 text-xs text-muted-foreground border-b border-border uppercase">
              <span>Produkt</span><span>Antal</span><span>Förp.</span><span>Pris</span>
            </div>
            {lines.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">Inga sparade rader (äldre faktura).</p>
            ) : lines.map((l, i) => (
              <div key={i} className="grid grid-cols-[1.6fr_.6fr_.9fr_1fr] gap-2 px-3 py-2 border-b border-border last:border-0 text-sm items-center">
                <span className="font-medium truncate">{l.name}</span>
                <span className="text-muted-foreground">{l.qty}</span>
                <span className="text-muted-foreground">{l.pack_size} {l.pack_unit}</span>
                <span className="font-mono">{kr(l.pack_price)}</span>
              </div>
            ))}
            {lines.length > 0 && (
              <div className="flex items-center justify-between px-3 py-3 bg-secondary/50 text-sm font-medium">
                <span>Totalt fakturavärde</span>
                <span className="font-mono" data-testid={`invoice-detail-total-${inv.id}`}>{kr(total)}</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceRow({ inv }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`invoice-${inv.id}`}
        className="w-full flex items-center gap-3 p-4 text-sm text-left hover:bg-secondary/40 transition-colors">
        <InvoiceThumb inv={inv} />
        <span className="font-medium">{inv.supplier || inv.file_name || "Faktura"}</span>
        <span className="text-muted-foreground">· {inv.line_count} rader</span>
        {inv.total != null && <span className="text-muted-foreground font-mono">· {kr(inv.total)}</span>}
        <span className="text-muted-foreground ml-auto font-mono text-xs">{new Date(inv.created_at).toLocaleDateString("sv-SE")}</span>
        <Badge tone="success">Godkänd</Badge>
      </button>
      <InvoiceDetail inv={inv} open={open} onOpenChange={setOpen} />
    </>
  );
}

/* ---------------- Fakturor ---------------- */
function InvoicesTab({ rid, invoices, actor }) {
  const [files, setFiles] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [batches, setBatches] = useState(null);
  const [drag, setDrag] = useState(false);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (arr.length) setFiles((f) => [...f, ...arr]);
  };
  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));

  const scan = async () => {
    if (files.length === 0) return toast.error("Välj en eller flera filer först");
    const { data: sess } = await supabase.auth.getSession();
    const token = sess?.session?.access_token;
    if (!token) return toast.error("Din session har gått ut – logga in igen");
    setScanning(true);
    setProgress({ done: 0, total: files.length });
    const results = [];
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const compressed = await compressImage(files[i], 2000, 0.9);
        const att = await uploadAttachment({ restaurantId: rid, entityType: "invoice", file: compressed });
        const url = await getAttachmentUrl(att.path, 600);
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoice-ocr`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ image_url: url }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.detail || "AI-avläsning misslyckades");
        const parsed = (data.lines || []).map((l) => ({
          id: newId(), name: l.name || "", supplier: l.supplier || data.supplier || "",
          qty: Number(l.qty) || 1, pack_size: Number(l.pack_size) || 1,
          pack_unit: l.pack_unit || "st", pack_price: Number(l.pack_price) || 0,
        }));
        const rank = { ny: 0, prisandring: 1, kand: 2 };
        const finalLines = (parsed.length ? parsed : [{ id: newId(), name: "", supplier: data.supplier || "", qty: 1, pack_size: 1, pack_unit: "kg", pack_price: 0 }])
          .sort((a, b) => (rank[ingredientMatchStatus(rid, a).status] ?? 0) - (rank[ingredientMatchStatus(rid, b).status] ?? 0));
        results.push({ id: newId(), fileName: files[i].name, photo: att, lines: finalLines });
      } catch (e) {
        failed++;
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setScanning(false);
    setProgress(null);
    if (results.length === 0) return toast.error("Ingen faktura kunde läsas av");
    setBatches(results);
    setFiles([]);
    toast.success(`${results.length} faktura(or) avläst${failed ? ` · ${failed} misslyckades` : ""}`);
  };

  const setLine = (bid, lid, patch) => setBatches((bs) => bs.map((b) => (b.id === bid ? { ...b, lines: b.lines.map((l) => (l.id === lid ? { ...l, ...patch } : l)) } : b)));
  const addLine = (bid) => setBatches((bs) => bs.map((b) => (b.id === bid ? { ...b, lines: [...b.lines, { id: newId(), name: "", supplier: "", qty: 1, pack_size: 1, pack_unit: "kg", pack_price: 0 }] } : b)));
  const removeLine = (bid, lid) => setBatches((bs) => bs.map((b) => (b.id === bid ? { ...b, lines: b.lines.filter((l) => l.id !== lid) } : b)));
  const removeBatch = (bid) => setBatches((bs) => { const next = bs.filter((b) => b.id !== bid); return next.length ? next : null; });

  const approveAll = () => {
    let saved = 0, prices = 0;
    (batches || []).forEach((b) => {
      const valid = b.lines.filter((l) => l.name.trim() && Number(l.pack_price) > 0);
      if (valid.length === 0) return;
      approveInvoice(rid, { supplier: valid[0].supplier, file_name: b.fileName, photo_bucket: b.photo?.bucket, photo_path: b.photo?.path, lines: valid }, actor);
      saved++; prices += valid.length;
    });
    if (saved === 0) return toast.error("Fyll i minst en rad med namn och pris");
    toast.success(`${saved} faktura(or) sparade · ${prices} priser uppdaterade`);
    setBatches(null); setFiles([]);
  };

  if (batches) {
    return (
      <div data-testid="invoice-review">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline text-muted-foreground">Granska & godkänn ({batches.length} {batches.length === 1 ? "faktura" : "fakturor"})</p>
            <p className="text-sm text-muted-foreground">Avvikande rader visas överst: <span className="text-[hsl(232_90%_78%)]">Ny produkt</span> och <span className="text-[hsl(38_90%_62%)]">prisändring &gt;5%</span>. Kända rader (samma pris) är nedtonade – godkänn allt med en knapp.</p>
          </div>
          <Button variant="ghost" onClick={() => { setBatches(null); setFiles([]); }} data-testid="invoice-cancel">Avbryt</Button>
        </div>
        <div className="space-y-6">
          {batches.map((b, bi) => (
            <div key={b.id} data-testid={`invoice-batch-${b.id}`} className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/30">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium text-sm truncate">{b.fileName || `Faktura ${bi + 1}`}</span>
                {(() => { const dev = b.lines.filter((l) => ingredientMatchStatus(rid, l).status !== "kand").length;
                  return <span className="text-xs text-muted-foreground">· {b.lines.length} rader{dev > 0 ? ` · ${dev} att granska` : " · alla kända"}</span>; })()}
                {batches.length > 1 && (
                  <button onClick={() => removeBatch(b.id)} className="ml-auto text-xs text-muted-foreground hover:text-[hsl(0_80%_72%)] inline-flex items-center gap-1" data-testid={`invoice-batch-remove-${b.id}`}><X className="h-3.5 w-3.5" />Ta bort faktura</button>
                )}
              </div>
              <div className="overflow-x-auto">
                <div className="min-w-[940px]">
                  <div className="grid grid-cols-[96px_1.5fr_1.3fr_.7fr_.8fr_.7fr_.9fr_.9fr_auto] gap-2 px-3 py-2 text-xs text-muted-foreground border-b border-border uppercase">
                    <span>Status</span><span>Produkt</span><span>Leverantör</span><span>Antal</span><span>Förp.</span><span>Enhet</span><span>Pris</span><span>Pris/enhet</span><span></span>
                  </div>
                  {b.lines.map((l) => {
                    const ppb = pricePerBase(l.pack_price, l.pack_size, l.pack_unit);
                    const st = ingredientMatchStatus(rid, l);
                    const dim = st.status === "kand";
                    return (
                      <div key={l.id} data-testid={`invoice-line-${l.id}`} className={`grid grid-cols-[96px_1.5fr_1.3fr_.7fr_.8fr_.7fr_.9fr_.9fr_auto] gap-2 px-3 py-2 border-b border-border last:border-0 items-center transition-opacity ${dim ? "opacity-55" : ""}`}>
                        <span data-testid={`invoice-status-${l.id}`} className="flex flex-col gap-0.5">
                          {st.status === "ny" && <Badge tone="info">Ny</Badge>}
                          {st.status === "prisandring" && <><Badge tone="warning">{st.changePct > 0 ? "+" : ""}{st.changePct}%</Badge><span className="text-[10px] text-muted-foreground font-mono">var {st.oldPrice} kr</span></>}
                          {st.status === "kand" && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Check className="h-3.5 w-3.5" />Känd</span>}
                        </span>
                        <Input value={l.name} onChange={(e) => setLine(b.id, l.id, { name: e.target.value })} className="h-8" data-testid={`invoice-name-${l.id}`} />
                        <Input value={l.supplier} onChange={(e) => setLine(b.id, l.id, { supplier: e.target.value })} className="h-8" />
                        <Input type="number" step="any" value={l.qty} onChange={(e) => setLine(b.id, l.id, { qty: e.target.value })} className="h-8" />
                        <Input type="number" step="any" value={l.pack_size} onChange={(e) => setLine(b.id, l.id, { pack_size: e.target.value })} className="h-8" />
                        <Select value={l.pack_unit} onValueChange={(v) => setLine(b.id, l.id, { pack_unit: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
                        <Input type="number" step="any" value={l.pack_price} onChange={(e) => setLine(b.id, l.id, { pack_price: e.target.value })} className="h-8" />
                        <span className="text-xs font-mono text-muted-foreground">{krUnit(ppb)}/{baseUnitOf(l.pack_unit)}</span>
                        <button onClick={() => removeLine(b.id, l.id)} className="text-muted-foreground hover:text-[hsl(0_80%_72%)]" data-testid={`invoice-remove-${l.id}`}><X className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="px-3 py-2 border-t border-border">
                <Button variant="secondary" size="sm" onClick={() => addLine(b.id)} data-testid={`invoice-add-line-${b.id}`}><Plus className="h-4 w-4 mr-1" />Lägg till rad</Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center mt-4">
          <Button onClick={approveAll} className="bg-accent text-white hover:opacity-90 ml-auto" data-testid="invoice-approve"><Check className="h-4 w-4 mr-1" />Godkänn alla & spara priser</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="invoices-tab">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-accent bg-accent/5" : "border-border"}`}
        data-testid="invoice-dropzone">
        <div className="h-12 w-12 mx-auto grid place-items-center rounded-2xl bg-secondary text-muted-foreground mb-4"><Upload className="h-6 w-6" /></div>
        <p className="font-display font-semibold">Ladda upp eller fotografera fakturor</p>
        <p className="text-sm text-muted-foreground mt-1">Dra in en eller flera filer här, eller välj PDF / foto. Du kan skanna flera på en gång.</p>
        {files.length > 0 && (
          <div className="mt-4 max-w-md mx-auto space-y-2 text-left">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm bg-secondary/40 rounded-lg px-3 py-2" data-testid={`invoice-selected-${i}`}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-[hsl(0_80%_72%)] shrink-0" data-testid={`invoice-selected-remove-${i}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-center gap-3 mt-5">
          <label className="cursor-pointer">
            <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="invoice-file" />
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary/60 border border-border text-sm hover:text-foreground"><FileText className="h-4 w-4" />Välj filer</span>
          </label>
          <Button onClick={scan} disabled={scanning} className="bg-accent text-white hover:opacity-90" data-testid="invoice-scan">
            {scanning
              ? <><Sparkles className="h-4 w-4 mr-1 animate-pulse" />Läser av{progress ? ` ${progress.done}/${progress.total}` : ""}…</>
              : <><Sparkles className="h-4 w-4 mr-1" />Läs av med AI{files.length > 1 ? ` (${files.length})` : ""}</>}
          </Button>
        </div>
      </div>

      <div className="mt-8">
        <p className="overline text-muted-foreground mb-3">Tidigare fakturor</p>
        {invoices.length === 0 ? <EmptyState title="Inga godkända fakturor ännu" /> : (
          <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
            {invoices.map((inv) => <InvoiceRow key={inv.id} inv={inv} />)}
          </div>
        )}
      </div>
    </div>
  );
}
